import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { READINGS } from "./Readings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", birth_date: "", birth_time: "", birth_place: "", preferred_time: "", intention: "" };

export const BookingForm = ({ selectedReading, onReadingChange }) => {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/bookings`, { ...form, reading: selectedReading });
            toast.success("Your request is with Mara — nothing is charged until she accepts.");
            setForm(initial);
        } catch {
            toast.error("The mirror clouded. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-purple-500/30 bg-[#0b031e]/70 px-4 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:shadow-[0_0_20px_rgba(199,125,255,0.25)] focus:outline-none";

    return (
        <section id="booking" data-testid="booking-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob left-[20%] top-[0%] h-[26rem] w-[26rem] bg-[#7b2cbf]/20" />
            <div className="mx-auto max-w-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mb-12 text-center"
                >
                    <p className="eyebrow mb-4">Initiate Your Reading</p>
                    <h2 className="text-glow-lilac font-display text-2xl font-bold tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                        STEP TOWARD THE MIRROR
                    </h2>
                    <p className="mx-auto mt-6 max-w-xl font-cormorant text-lg italic text-[#e0aaff]/80">
                        Give the sisters your coordinates in this realm — the moment and place your thread began — and
                        they will find her on the other side. You pay nothing now: Mara weighs every request herself,
                        and only after she accepts do you receive the payment mirror.
                    </p>
                </motion.div>

                <motion.form
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    onSubmit={submit}
                    data-testid="booking-form-container"
                    className="glass-card relative overflow-hidden p-8 sm:p-10"
                >
                    <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-20" />
                    <div className="relative grid gap-6 sm:grid-cols-2">
                        <div>
                            <label htmlFor="booking-name" className="eyebrow mb-2 block">Your name</label>
                            <input id="booking-name" required value={form.name} onChange={set("name")} data-testid="booking-input-name" placeholder="What this realm calls you" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="booking-email" className="eyebrow mb-2 block">Email</label>
                            <input id="booking-email" required type="email" value={form.email} onChange={set("email")} data-testid="booking-input-email" placeholder="Where the answer should arrive" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="booking-birthdate" className="eyebrow mb-2 block">Birth date</label>
                            <input id="booking-birthdate" required type="date" value={form.birth_date} onChange={set("birth_date")} data-testid="booking-input-birthdate" className={`${inputCls} [color-scheme:dark]`} />
                        </div>
                        <div>
                            <label htmlFor="booking-birthtime" className="eyebrow mb-2 block">Birth time (if known)</label>
                            <input id="booking-birthtime" type="time" value={form.birth_time} onChange={set("birth_time")} data-testid="booking-input-birthtime" className={`${inputCls} [color-scheme:dark]`} />
                        </div>
                        <div>
                            <label htmlFor="booking-birthplace" className="eyebrow mb-2 block">Birth place</label>
                            <input id="booking-birthplace" value={form.birth_place} onChange={set("birth_place")} data-testid="booking-input-birthplace" placeholder="City, country" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="booking-reading" className="eyebrow mb-2 block">Chosen reading</label>
                            <select
                                id="booking-reading"
                                required
                                value={selectedReading}
                                onChange={(e) => onReadingChange(e.target.value)}
                                data-testid="booking-select-reading"
                                className={`${inputCls} appearance-none`}
                            >
                                <option value="" disabled>Select your crossing…</option>
                                {READINGS.map((r) => (
                                    <option key={r.id} value={r.id} className="bg-[#0b031e]">
                                        {r.title} — {r.price}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="booking-preferred-time" className="eyebrow mb-2 block">Preferred time</label>
                            <input
                                id="booking-preferred-time"
                                value={form.preferred_time}
                                onChange={set("preferred_time")}
                                data-testid="booking-input-preferred-time"
                                placeholder="When the mirror should open — day and hour"
                                className={inputCls}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="booking-intention" className="eyebrow mb-2 block">Your intention</label>
                            <textarea
                                id="booking-intention"
                                rows={4}
                                value={form.intention}
                                onChange={set("intention")}
                                data-testid="booking-input-intention"
                                placeholder="Tell the mirror what you are seeking — the blessing that went to her, the door that closed, the life you suspect is being lived without you…"
                                className={`${inputCls} resize-none`}
                            />
                        </div>
                    </div>
                    <button type="submit" disabled={submitting} data-testid="booking-submit-button" className="btn-glow relative mt-8 flex w-full items-center justify-center gap-3 disabled:opacity-70">
                        <span className="mirror-sheen absolute inset-0 animate-shimmer-line" />
                        <Send className="relative h-4 w-4" />
                        <span className="relative">{submitting ? "OPENING THE PORTAL…" : "SUBMIT TO THE MIRROR"}</span>
                    </button>
                </motion.form>
            </div>
        </section>
    );
};
